import { useEffect, useId, useRef } from 'react';
import { useSite } from '../../app/site';
import { ACCEPT_ATTR } from '../../config';
import { CameraIcon, FileIcon } from '../../components/Icons';
import { useT, type DictKey } from '../../i18n';
import { formatBytes } from '../../lib/format';
import type { UploadErrorCode } from './uploadFlow';
import { useUploads } from './uploadStore';

const ERR: Record<UploadErrorCode, DictKey> = {
  too_large: 'upload.tooLarge',
  bad_type: 'upload.badType',
  empty: 'upload.empty',
  network: 'err.network',
  rate_limited: 'err.rate_limited',
  failed: 'upload.failed',
};

interface Props {
  itemId: string;
  caseId: string;
  documentTypeKey?: string;
  /** label of the document, for screen readers */
  label: string;
  replace?: boolean;
  onDone: () => void;
}

/** Two big buttons: camera (capture) and file picker; then progress / retry. */
export function UploadControl({ itemId, caseId, documentTypeKey, label, replace, onDone }: Props) {
  const t = useT();
  const id = useId();
  const camRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const entry = useUploads((s) => s.entries[itemId]);
  // ліміт з GET /public/site, у мегабайтах для тексту («до 20 МБ»)
  const mb = Math.round(useSite((s) => s.maxUploadBytes) / 1024 / 1024);
  const { start, retry, cancel, clear } = useUploads.getState();
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    if (entry?.phase === 'done') {
      doneRef.current();
      const tm = setTimeout(() => clear(itemId), 6000);
      return () => clearTimeout(tm);
    }
  }, [entry?.phase, itemId, clear]);

  const pick = (files: FileList | null) => {
    const f = files?.[0];
    if (f) void start(itemId, { caseId, file: f, documentTypeKey });
  };

  const busy = entry && (entry.phase === 'preparing' || entry.phase === 'uploading' || entry.phase === 'finishing');
  const pct = Math.round((entry?.progress ?? 0) * 100);

  if (busy && entry) {
    return (
      <div className="mt-4" aria-live="polite">
        <p className="text-[15px]">
          {entry.phase === 'preparing' ? t('upload.preparing') : entry.phase === 'finishing' ? t('upload.finishing') : t('upload.uploading', { pct })}
        </p>
        <div
          className="mt-3 h-[3px] bg-line"
          role="progressbar"
          aria-label={`${t('upload.progress')}: ${label}`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={pct}
        >
          <i className="block h-[3px] bg-fg transition-[width] duration-200" style={{ width: `${entry.phase === 'finishing' ? 100 : pct}%` }} />
        </div>
        <p className="mt-2 truncate text-[13px] text-lab">
          {entry.fileName} · {formatBytes(entry.fileSize)}
        </p>
        {entry.phase !== 'finishing' ? (
          <button type="button" className="link mt-1" onClick={() => cancel(itemId)}>
            {t('upload.cancel')}
          </button>
        ) : null}
      </div>
    );
  }

  const canRetry = entry?.phase === 'error' && entry.error && !['too_large', 'bad_type', 'empty'].includes(entry.error);

  return (
    <div className="mt-4">
      {entry?.phase === 'done' ? (
        <p role="status" className="mb-3 text-[15px]">
          {t('upload.done')}
        </p>
      ) : null}
      {entry?.phase === 'error' && entry.error ? (
        <div role="alert" className="mb-4 border-l-2 border-bad py-1 pl-4 text-[15px]">
          <p>{t(ERR[entry.error], { size: formatBytes(entry.fileSize), mb })}</p>
          {canRetry ? (
            <button type="button" className="btn-quiet mt-3" onClick={() => void retry(itemId)}>
              {t('upload.retry')}
            </button>
          ) : null}
        </div>
      ) : null}

      {entry?.phase !== 'done' ? (
        <>
          <div className="grid grid-cols-1 gap-3 min-[380px]:grid-cols-2">
            <button type="button" className="btn-quiet min-h-[52px]" onClick={() => camRef.current?.click()} aria-describedby={`${id}-lim`}>
              <CameraIcon /> {t('upload.takePhoto')}
            </button>
            <button type="button" className="btn-quiet min-h-[52px]" onClick={() => fileRef.current?.click()} aria-describedby={`${id}-lim`}>
              <FileIcon /> {replace ? t('upload.replace') : t('upload.chooseFile')}
            </button>
          </div>
          <p id={`${id}-lim`} className="mt-2 text-[13px] text-lab">
            {t('upload.limits', { mb })}
          </p>
        </>
      ) : null}

      {/* hidden native inputs; values reset so the same file can be picked again after an error */}
      <input
        ref={camRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT_ATTR}
        className="sr-only"
        tabIndex={-1}
        aria-hidden
        onChange={(e) => {
          pick(e.target.files);
          e.target.value = '';
        }}
      />
    </div>
  );
}
